import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
// Type-only: `tree-view.component` pulls in the Syncfusion tree, and this leaf component has no
// runtime need for any of it.
import type { OrgTreeView } from '../tree-view/tree-view.component';

/**
 * The ancestor chain above a node, root first - "Tanklink > Tanklink Direct".
 *
 * Organization names repeat across billable trees, so the path is often the only thing that
 * distinguishes two identically named nodes. It is still secondary to whatever it sits beside:
 * it takes only leftover space, and when the whole path will not fit it hides rather than showing
 * a truncated one. Half a path is worse than none - "Tanklink > Afton Chemical Fuel Ad..." reads
 * as a different organization from the one it actually names.
 *
 * Give the host a zero flex basis (`class="min-w-0 flex-1"`) so it cannot claim width from its
 * neighbour; the full path stays reachable as a tooltip either way.
 */
@Component({
  selector: 'app-tree-breadcrumb',
  templateUrl: './tree-breadcrumb.component.html',
})
export class TreeBreadcrumbComponent implements AfterViewInit, OnDestroy {
  @Input() path: OrgTreeView[] = [];

  /** The space actually available - whatever the row had left over. */
  @ViewChild('track', { static: true }) private track: ElementRef<HTMLElement>;

  /** The path at its natural width, which overflows the track when there is not room. */
  @ViewChild('content', { static: true }) private content: ElementRef<HTMLElement>;

  fits = true;

  private observer: ResizeObserver | null = null;

  constructor(
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  get fullPath(): string {
    return this.path.map(node => node.name).join(' › ');
  }

  ngAfterViewInit(): void {
    // Not available in jsdom, so specs measure nothing and the path simply stays visible.
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    // Outside the zone: this fires on every layout change of either box, and none of them need a
    // change detection pass unless the answer actually flips.
    this.zone.runOutsideAngular(() => {
      this.observer = new ResizeObserver(() => this.measure());
      this.observer.observe(this.track.nativeElement);

      // Watching the content too is what picks up a new path: replacing the text resizes this box
      // even while it is hidden, which the track alone would never report.
      this.observer.observe(this.content.nativeElement);
    });

    this.measure();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  /**
   * Hiding uses `opacity`, never `display` or `visibility`, and the measurement reads the CONTENT
   * rather than the track.
   *
   * `display: none` would drop the width to zero, zero always "fits", and it would be shown again
   * on the next pass - a permanent flicker. `visibility: hidden` keeps the box but takes the path
   * out of the accessibility tree, which both loses it for screen readers and churns the
   * `aria-live` region it sits inside. `opacity` changes neither layout nor semantics, so the
   * measurement is stable and the full path is still announced.
   */
  private measure(): void {
    const track = this.track?.nativeElement;
    const content = this.content?.nativeElement;
    if (!track || !content) {
      return;
    }

    const fits = content.scrollWidth <= track.clientWidth;
    if (fits === this.fits) {
      return;
    }

    this.zone.run(() => {
      this.fits = fits;
      this.cdr.detectChanges();
    });
  }
}
